// Button.tsx — Three-variant button primitive bound to design tokens.
import React, { useState } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
}

export const Button: React.FC<ButtonProps> = ({
    variant = 'secondary',
    type = 'button',
    style,
    disabled,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    children,
    ...rest
}) => {
    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);

    let background: string;
    let color: string;
    let border: string;

    if (variant === 'primary') {
        background = hover ? 'var(--color-brand-strong)' : 'var(--color-brand)';
        color = hover ? 'var(--color-on-brand-strong)' : 'var(--color-on-brand)';
        border = '1px solid transparent';
    } else if (variant === 'secondary') {
        background = hover ? 'var(--color-bg-card-hover)' : 'var(--color-bg-card)';
        color = 'var(--color-text-primary)';
        border = '1px solid var(--color-border-subtle)';
    } else {
        background = 'transparent';
        color = 'var(--color-text-secondary)';
        border = '1px solid transparent';
    }

    const baseStyle: React.CSSProperties = {
        height: '36px',
        padding: '0.5rem 1rem',
        background,
        color,
        border,
        borderRadius: 'var(--radius-default)',
        fontFamily: 'var(--type-label-family)',
        fontSize: 'var(--type-label-size)',
        fontWeight: 'var(--type-label-weight)' as React.CSSProperties['fontWeight'],
        lineHeight: 'var(--type-label-line-height)',
        letterSpacing: 'var(--type-label-letter-spacing)',
        textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        outline: 'none',
        boxShadow: focus
            ? '0 0 0 1px var(--color-border-strong), 0 0 0 3px color-mix(in srgb, var(--color-brand) 40%, transparent)'
            : 'none',
        transition: 'background 120ms ease, color 120ms ease',
        ...style,
    };

    return (
        <button
            type={type}
            disabled={disabled}
            style={baseStyle}
            onMouseEnter={(e) => { setHover(true); onMouseEnter?.(e); }}
            onMouseLeave={(e) => { setHover(false); onMouseLeave?.(e); }}
            onFocus={(e) => { setFocus(true); onFocus?.(e); }}
            onBlur={(e) => { setFocus(false); onBlur?.(e); }}
            {...rest}
        >
            {children}
        </button>
    );
};

export default Button;
